/**
 * Mock data for Unconstrained Forecast Updated (15-month) mockup.
 * No API calls -- purely static data for UI prototyping.
 */

import {
  singleConceptInterface,
  singleContractInterface,
  notesInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';

// ---------------------------------------------------------------------------
// Future months helper (15 months starting from next month)
// ---------------------------------------------------------------------------

export interface PlanningMonth {
  label: string;
  dateString: string;
  /** true for months 13-15 (the 3 additional extended forecast months) */
  isExtended: boolean;
}

export const getFutureMonths15 = (): PlanningMonth[] => {
  const arr: PlanningMonth[] = [];
  const dt = new Date();
  const startMonth = dt.getMonth();
  const startYear = dt.getFullYear();
  const monthShortHand = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  for (let i = 0; i < 15; i++) {
    const month = (startMonth + i + 1) % 12;
    const year = startYear + Math.floor((startMonth + i + 1) / 12);
    arr.push({
      label: `${monthShortHand[month]}-${year}`,
      dateString: `${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`,
      isExtended: i >= 12,
    });
  }
  return arr;
};

// Pre-compute months so all data uses the same set
const PLANNING_MONTHS = getFutureMonths15();

// ---------------------------------------------------------------------------
// Concept details — matches singleConceptInterface (used by ConceptDetailsSection)
// ---------------------------------------------------------------------------

export const mockConceptDetails: singleConceptInterface = {
  concept: 'Amlodipine Besylate 10mg Tablet',
  gfc: 67890,
  generic_rxcui: '17767',
  therapeutic_category: 'Cardiovascular',
  pharmacologic_class: 'Calcium Channel Blockers',
  va_class_name: 'CV200',
  des_schedule: 'N/A',
  duration_condition: 'Chronic',
  Strength: '10mg',
};

// ---------------------------------------------------------------------------
// Contract details — matches singleContractInterface[] (used by ConceptDetailsSection)
// ---------------------------------------------------------------------------

export const mockContractDetails: singleContractInterface[] = [
  {
    GFC: 67890,
    ContractNumber: 'SPE2D1-25-D-0055',
    ContractType: 'VA FSS',
    StartDate: '2025-03-01',
    EndDate: '2030-02-28',
  },
];

// ---------------------------------------------------------------------------
// Notes mock data — matches notesInterface[] (used by NotesSection)
// ---------------------------------------------------------------------------

export const mockNotesData: notesInterface[] = [
  {
    ID: 1,
    Notes: 'Unconstrained forecast reviewed for Q4 2025. Model-based strategy applied to all NDCs.',
    CreatedBy: 'John Smith',
    CreatedDate: '2025-10-20T09:00:00Z',
  },
  {
    ID: 2,
    Notes: 'Extended forecast months (13-15) reflect seasonal demand adjustments based on historical VA utilisation patterns.',
    CreatedBy: 'Jane Doe',
    CreatedDate: '2025-11-05T14:30:00Z',
  },
];

// ---------------------------------------------------------------------------
// Planning periods for "Choose Plan" Dropdown
// ---------------------------------------------------------------------------

export interface PlanningPeriod {
  label: string;
  value: string;
}

export const mockPlanningPeriods: PlanningPeriod[] = [
  { label: 'Oct 2024 - Sep 2025', value: '2024-10-01' },
  { label: 'Oct 2025 - Sep 2026', value: '2025-10-01' },
];

// ---------------------------------------------------------------------------
// NDC headings
// ---------------------------------------------------------------------------

export interface NDCHeading {
  ndc: string;
  packSize: string;
}

export const mockDirectNDCs: NDCHeading[] = [
  { ndc: '67890-1234-01', packSize: '30' },
  { ndc: '67890-1234-02', packSize: '90' },
];

export const mockIndirectNDCs: NDCHeading[] = [
  { ndc: '67890-5678-01', packSize: '30' },
];

// ---------------------------------------------------------------------------
// Forecast detail interfaces & data
// ---------------------------------------------------------------------------

export interface ForecastDetail {
  ForecastPeriod: string;
  StratCode: string;
  WholesalerDemandForecast: number;
  UnconstrainedForecast: number;
}

export interface NDCForecast {
  NDC11: string;
  SellingCategory: string;
  forecast_details: ForecastDetail[];
}

/** Helper: build 15 forecast detail rows with StratCode = 'Model' */
function buildForecastDetails(
  baselineStart: number,
  adjOffset: number,
): ForecastDetail[] {
  return PLANNING_MONTHS.map((m, i) => {
    const baseline = Math.round(baselineStart + i * 320 + Math.sin(i) * 800);
    const adj = Math.round(baseline + adjOffset + Math.cos(i) * 400);
    return {
      ForecastPeriod: m.dateString,
      StratCode: 'Model',
      WholesalerDemandForecast: baseline,
      UnconstrainedForecast: adj,
    };
  });
}

export const mockForecastData: NDCForecast[] = [
  {
    NDC11: '67890-1234-01',
    SellingCategory: 'Direct',
    forecast_details: buildForecastDetails(12000, 500),
  },
  {
    NDC11: '67890-1234-02',
    SellingCategory: 'Direct',
    forecast_details: buildForecastDetails(18000, -300),
  },
  {
    NDC11: '67890-5678-01',
    SellingCategory: 'Indirect',
    forecast_details: buildForecastDetails(8000, 200),
  },
];

// ---------------------------------------------------------------------------
// Invoice Sales summary
// ---------------------------------------------------------------------------

export interface InvoiceSales {
  NDC11: string;
  YTDAverage: number;
  ThreeMonthAverage: number;
  LastMonthAverage: number;
}

export const mockInvoiceSales: InvoiceSales[] = [
  { NDC11: '67890-1234-01', YTDAverage: 13250, ThreeMonthAverage: 12800, LastMonthAverage: 13100 },
  { NDC11: '67890-1234-02', YTDAverage: 19400, ThreeMonthAverage: 18750, LastMonthAverage: 19200 },
  { NDC11: '67890-5678-01', YTDAverage: 8650, ThreeMonthAverage: 8300, LastMonthAverage: 8500 },
];
